/**
 * P5.2 D — coming back from a payment redirect (Klarna, Affirm, a bank).
 *
 * Stripe sends the customer back to the page they paid on with our resume
 * token in the URL (`sb_resume`) alongside its own params. The token is
 * read ONCE, as soon as widget.js runs, and stripped from the address bar
 * with everything Stripe added: it is single-use, and leaving it in the URL
 * only hands it to whatever the page logs next. (It must be treated as
 * already leaked anyway — the salon's analytics can read the URL before we
 * run — which is why the server stores only a hash and spends it once.)
 *
 * Redeeming is shared per page load. A page can end up with more than one
 * widget instance (an auto-open and the salon's own open on `book=1`, or a
 * close-and-reopen), and the token can only be spent once — so every
 * instance gets the SAME answer rather than the second getting a refusal.
 */

export const RESUME_PARAM = 'sb_resume';
const STRIPE_RETURN_PARAMS = ['payment_intent', 'payment_intent_client_secret', 'redirect_status'];

let token: string | null = null;
let captured = false;
/** Keyed by merchant: one salon's widget must not spend — or fail — another's. */
const redemptions = new Map<string, Promise<unknown>>();

/**
 * Read and strip the token from the current URL. Idempotent.
 *
 * The server puts it in the FRAGMENT (`#sb_resume=…`), which no server,
 * Referer or common analytics tag sees; the query is read too, for a link
 * minted before that change.
 */
export function captureResumeToken(loc: Location = window.location): string | null {
  if (captured) return token;
  captured = true;
  try {
    const url = new URL(loc.href);
    const fromHash = new URLSearchParams(url.hash.replace(/^#/, '')).get(RESUME_PARAM);
    token = fromHash ?? url.searchParams.get(RESUME_PARAM);
    if (token) {
      url.searchParams.delete(RESUME_PARAM);
      for (const p of STRIPE_RETURN_PARAMS) url.searchParams.delete(p);
      if (fromHash) url.hash = '';
      window.history.replaceState(window.history.state, '', url.toString());
    }
  } catch {
    token = null;
  }
  return token;
}

/** The captured token, if this page load came back from a redirect. */
export function pendingResumeToken(): string | null {
  return token;
}

/**
 * Spend the token once per page load PER MERCHANT; every widget for that
 * merchant shares the result. Keyed because a page can carry widgets for
 * more than one salon, and the token belongs to exactly one: another
 * salon's widget asking first would be refused (tenant-scoped), and a
 * shared answer would hand that refusal to the right one. Resolves null
 * when there is no token or the server refused it — the caller then
 * carries on as a normal, fresh visit.
 */
export function redeemOnce<T>(
  merchant: string,
  redeem: (token: string) => Promise<T>,
): Promise<T | null> {
  if (!token) return Promise.resolve(null);
  let pending = redemptions.get(merchant);
  if (!pending) {
    pending = redeem(token).catch(() => null);
    redemptions.set(merchant, pending);
  }
  return pending as Promise<T | null>;
}

/**
 * Finish the resumed booking ONCE per merchant per page, and give every
 * widget instance the same outcome. Two checkouts would race the same
 * cart under different idempotency keys, and the loser would show an
 * error for a booking that succeeded — so the first instance to ask runs
 * it and the rest await its result. A follower used to sit on Confirm
 * waiting for a tap, which read as "not booked yet" to a customer who
 * had just paid (Be You, 2026-10-12).
 *
 * `leader` is true for the instance that ran it: only that one reports
 * the booking to analytics, so a page with two instances counts one.
 */
const checkouts = new Map<string, Promise<unknown>>();
export function checkoutResumedOnce<T>(
  merchant: string,
  run: () => Promise<T>,
): { result: Promise<T>; leader: boolean } {
  const existing = checkouts.get(merchant);
  if (existing) return { result: existing as Promise<T>, leader: false };
  const result = run();
  checkouts.set(merchant, result);
  return { result, leader: true };
}

/** Test-only. */
export function resetResumeForTests(): void {
  checkouts.clear();
  token = null;
  captured = false;
  redemptions.clear();
}
